import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SuppliesProductSubCategory } from '../../../../../models/suppliesSubCategory';
import { SuppliesCategoryProduct } from '../../../../../models/suppliesCategoryProduct';
import { generateImageFileNameWithRandomName } from '../../../../../utils/generateImageFileName';
import axios from '../../../../../lib/axios';
import { MASTER_SUB_CATEGORIES_CREATE, MASTER_SUB_CATEGORIES_UPDATE } from '../../../../../lib/endpoints/category';
import { IMAGE_CATEGORY_ROOT_URL } from '../../../../../constants/imageUrl';

interface Props {
  onOpen: boolean;
  onClose: VoidFunction;
  selectedSubCat: SuppliesProductSubCategory;
  onfetch: VoidFunction;
  selected: Function;
  CatProId: SuppliesCategoryProduct;
}

export const useFormSubCategoryController = ({
  selectedSubCat,
  CatProId,
  selected,
  onfetch,
  onOpen,
  onClose,
}: Props) => {
  const [imageToShow, setImageToShow] = useState<any>(null!);
  const [imageFile, setImageFile] = useState<any>(null!);
  const [openConfirmForm, setOpenConfirmForm] = useState<boolean>(false);
  const [loadingConfirmButton, setLoadingConfirmButton] = useState<boolean>(false);
  const [imageError, setImageError] = useState<string>(null!);
  const [errorMessage, setErrorMessage] = useState<string>(null!);

  const subCode = useRef<HTMLInputElement>(null!);
  const nameLa = useRef<HTMLInputElement>(null!);
  const nameEn = useRef<HTMLInputElement>(null!);

  const { t } = useTranslation('global');

  const handleOpenForm = (e: any) => {
    e.preventDefault();
    if (handleValidateForm()) {
      setOpenConfirmForm(true);
    }
  };

  const handleCloseConfirmForm = () => {
    setOpenConfirmForm(false);
  };

  const handleReset = () => {
    selected(null);
    setImageToShow(null);
    setImageFile(null);
    subCode.current.value = '';
    nameLa.current.value = '';
    nameEn.current.value = '';
  };

  const handleValidateForm = () => {
    if (!selectedSubCat && !imageFile) {
      setImageError('Please selected Image.');
      return false;
    }
    if (imageFile && imageError) {
      setImageError('');
    }
    setErrorMessage('');
    return true;
  };

  const handleSelectImage = (e: any) => {
    const file = e.target.files[0];
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      if (reader.readyState === 2) {
        setImageToShow(reader.result);
      }
    };
    reader.readAsDataURL(file);
    setImageToShow(file);
  };

  const handleSubmit = () => {
    if (selectedSubCat?.id) {
      handleEditSubcategory();
    } else {
      handleAddSubCategory();
    }
  };

  const handleAddSubCategory = async () => {
    try {
      setLoadingConfirmButton(true);
      const FormInput = {
        code: subCode.current?.value,
        sub_category_name_la: nameLa.current?.value,
        sub_category_name_en: nameEn.current?.value,
        supplies_product_category_id: CatProId.id,
        file: generateImageFileNameWithRandomName(imageFile),
      };

      await axios.post(`${MASTER_SUB_CATEGORIES_CREATE}`, FormInput, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      onClose();
      handleReset();
      await onfetch();
    } catch (error: any) {
      try {
        const getErrorMessage: string = error.response.data.message;
        setErrorMessage(getErrorMessage);
      } catch (err) {
        setErrorMessage(t('Server error please try again') || '');
      }
    } finally {
      setLoadingConfirmButton(false);
      setOpenConfirmForm(false);
    }
  };

  const handleEditSubcategory = async () => {
    try {
      setLoadingConfirmButton(true);
      const FormEdit: any = {
        code: subCode.current?.value,
        sub_category_name_la: nameLa.current?.value,
        sub_category_name_en: nameEn.current?.value,
        supplies_product_category_id: CatProId.id,
      };
      if (imageFile) {
        FormEdit.file = generateImageFileNameWithRandomName(imageFile);
      }
      await axios.patch(
        `${MASTER_SUB_CATEGORIES_UPDATE + selectedSubCat.id}`,
        FormEdit,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      onClose();
      await onfetch();
      handleReset();
    } catch (error: any) {
      try {
        const getErrorMessage: string = error.response.data.message;
        setErrorMessage(getErrorMessage);
      } catch (err) {
        setErrorMessage(t('Server error please try again') || '');
      }
    } finally {
      setLoadingConfirmButton(false);
      setOpenConfirmForm(false);
    }
  };

  useEffect(() => {
    if (selectedSubCat) {
      subCode.current.value = selectedSubCat?.code;
      nameLa.current.value = selectedSubCat?.sub_category_name_la;
      nameEn.current.value = selectedSubCat?.sub_category_name_en;
      setImageToShow(IMAGE_CATEGORY_ROOT_URL + selectedSubCat?.icon?.url);
    }
  }, [selectedSubCat]);

  return {
    errorMessage,
    imageError,
    handleOpenForm,
    handleCloseConfirmForm,
    handleSelectImage,
    imageToShow,
    subCode,
    nameLa,
    nameEn,
    openConfirmForm,
    loadingConfirmButton,
    handleSubmit,
    handleReset,
  };
};
